/**
 * Portable brewauth admin app-access client.
 *
 * PORTABLE: this module must not import anything from the gui-react app
 * (no `./client`, `./oidc`, the Zustand store, or the router) — it is a
 * candidate for extraction into a shared `@brew/brewauth-client` package
 * (see the PROMOTE-LATER note below). `createBrewauthAdminClient` takes its
 * base URL and token getter as config so app-specific wiring (auth, base
 * path) lives in the caller instead of here.
 *
 * Talks to brewauth's admin app-access API, which the revproxy exposes
 * same-origin at `/api/brewauth/admin/app-access/*`
 * (`/Users/nick/work/lab/brewauth/internal/server/server.go`).
 *
 * JSON key casing: brewauth's `appaccess.Grant` Go struct
 * (`/Users/nick/work/lab/brewauth/internal/appaccess/store.go`) now carries
 * snake_case json tags — `profile_id`, `client_id`, `granted_by` (omitted
 * when empty), `granted_at`, `status`, `justification` (omitted when
 * empty), `requested_at` — and a custom `MarshalJSON` that emits
 * `granted_at`/`requested_at` as JSON `null` when the timestamp is a zero
 * `time.Time` (never the old `"0001-01-01T00:00:00Z"` sentinel), so a
 * genuinely-unknown timestamp is distinguishable from a real one. `norm()`
 * below reads the snake_case keys and, defensively, older PascalCase/
 * camelCase variants a prior server version may have sent, and treats a
 * `null` timestamp as absent rather than a date.
 *
 * Request bodies are the opposite: brewauth's `grantBody`/`bulkGrantBody`
 * structs DO carry snake_case json tags (`profile_id`, `client_id`,
 * `profile_ids`), which is what `grant`/`deny`/`bulkGrant` send below.
 *
 * The session methods below talk to a sibling admin API,
 * `/api/brewauth/admin/sessions*` (same server.go, not nested under
 * app-access). Those handlers' structs DO carry snake_case json tags
 * (`family_id`, `profile_id`, `client_id`, `issued_at`, `access_expires_at`,
 * and the event fields), so the norm* mappers below map a single fixed
 * snake_case shape — no PascalCase/camelCase defense needed there.
 *
 * Rich session shape: `sessionView`
 * (`/Users/nick/work/lab/brewauth/internal/server/admin.go`) backs both
 * `GET /admin/sessions` and `GET /admin/sessions/user/:profileID`. The list
 * handler filters/paginates server-side and reports a `total` alongside
 * `sessions`: the count of rows matching the filter before pagination is
 * applied, so callers can compute an accurate page range. `listSessions`
 * below reads that wire `total`; the `sessions.length` fallback only kicks
 * in against older backends that predate the field.
 *
 * Rich event shape: `historicalEventView` backs
 * `GET /admin/sessions/historical`. The older, narrower `eventView` shape
 * (`at`, `client_id`, `family_id`, `type`, `reason`, `actor` only) still
 * backs `GET /admin/sessions/user/:profileID/events` — kept server-side
 * alongside the new historical endpoint. `BrewauthSessionEvent` below is a
 * superset of both: the compact fields are always present, the rest are
 * optional and only populated when the row came from `listHistorical`.
 */

export interface AppGrant {
  profileId: string;
  clientId: string;
  status: 'approved' | 'pending' | 'denied';
  grantedBy?: string;
  grantedAt?: string;
  justification?: string;
  requestedAt?: string;
  /** A role the client declares; absent means the client's default role. */
  role?: string;
  /** An open request for a role. The grant's status stays approved meanwhile. */
  roleRequest?: AppRoleRequest;
}

export interface AppRoleRequest {
  role: string;
  justification?: string;
  requestedAt?: string;
}

/** One role a client declares in brewauth's clients.yaml. */
export interface AppRole {
  id: string;
  label: string;
}

/** What a client declares. `default` is what a grant with no role means, and is never stored. */
export interface AppClientRoles {
  default: AppRole;
  assignable: AppRole[];
}

/** Wire shape of brewauth's `clientView` (`internal/server/admin.go`) — the
 *  registered OIDC clients, so admin UIs stop hardcoding the app list. */
export interface AppClient {
  id: string;
  name: string;
  appUrl?: string;
  /** Absent for a client that declares no roles: it shows no role control. */
  roles?: AppClientRoles;
}

/** Wire shape of a registration Definition (brewauth `registration.Definition`).
 *  items/coreFields stay opaque here so this portable client needn't depend on
 *  @brew/ui's renderer types; the page casts to @brew/ui's `Definition`. */
export interface RegistrationDefinition {
  id?: string;
  label?: string;
  items?: unknown[];
  exclude?: string[];
  coreFields?: unknown;
  updated_at?: string;
  updated_by?: string;
}

export interface RegistrationValidationError {
  item_id: string;
  field: string;
  message: string;
}

/** Minimal, opaque registration item shape (the client stays portable —
 *  Task 3's resolver refines this via @brew/ui's `Item`). */
export interface RegistrationItem {
  id: string;
  name?: string;
  type?: string;
  [k: string]: unknown;
}

/** Wire shape of one stored registration answer (brewauth
 *  `registration.Answer`, `internal/registration/answers.go`): the value is
 *  a scalar string (option/optionFilter/boolean = the selected key), plus
 *  optional nested subgroup answers keyed by subgroup item id. A portable
 *  mirror of @brew/ui/registration's `Answer`/`Answers` — kept local so this
 *  client needn't depend on @brew/ui; the two are structurally identical. */
export interface RegistrationAnswer {
  value: string;
  subgroupValues?: Record<string, RegistrationAnswer>;
}
export type RegistrationAnswers = Record<string, RegistrationAnswer>;

export interface ProfileRegistration {
  items: RegistrationItem[];
  answers: RegistrationAnswers;
}

/**
 * Wire shape of brewauth's `credentialView` (`internal/server/admin_credentials.go`)
 * — one candidate migration target, listed in the 400 response `removeCredential`
 * answers when more than one credential would survive the removal. Attached
 * (camelCased) to the thrown error's `candidates` field so callers can raise a
 * target-picker without a second round-trip; see `reqAt`'s error handling below.
 */
export interface CredentialCandidate {
  subjectDn: string;
  issuerDn: string;
  isBasic: boolean;
  passwordExpired: boolean;
}

/**
 * Wire shape of a successful `updateCredentialDn` response
 * (`internal/server/admin_credentials.go`'s `renameCredential`). The rename
 * itself has already committed by the time this resolves — `warning` is
 * present only on the one path where the follow-up brewdb/brewlytics
 * resource-migration job failed to enqueue, and callers MUST surface it
 * rather than reporting unconditional success: the migration will not start
 * on its own and an operator needs to re-drive it.
 */
export interface CredentialRenameResult {
  status: string;
  subject: string;
  issuer: string;
  warning?: string;
}

/**
 * Wire shape of a successful `removeCredential` response
 * (`internal/server/admin_credentials.go`'s `removeCredential`). The removal
 * has already committed by the time this resolves; `job_id` names the
 * resource-migration job that was queued for it.
 *
 * `warning` is present only on the one path where that enqueue failed, and
 * it replaces `job_id` rather than accompanying it — nothing was queued.
 * Callers MUST surface it rather than reporting unconditional success: the
 * migration will not start on its own, and a removal is the sharper case of
 * the two (cf. `CredentialRenameResult`), because it leaves the resources
 * with no credential to migrate *from* — an operator who is not told has
 * little chance of noticing later.
 */
export interface CredentialRemoveResult {
  status: string;
  target?: string;
  job_id?: string;
  warning?: string;
}

/**
 * Inactivity auto-disable (jorigin/brewauth#143, brewlytics#2482). Wire values
 * are brewauth's `inactivity.Mode` — a contract, never renamed.
 */
export type InactivityMode = 'off' | 'dry_run' | 'enforcing';

/** The admin-editable inactivity settings (brewauth#146), by wire name. */
export type InactivitySettingField =
  | 'threshold_days'
  | 'warn_days'
  | 'max_disables_per_tick'
  | 'exempt_profiles'
  | 'coverage_min_rows'
  | 'coverage_tolerance';

export type InactivitySettingValue = number | number[] | string[] | string;

/** One setting as `GET /admin/inactivity` reports it. */
export interface InactivitySettingStatus {
  value: InactivitySettingValue;
  /** `admin` (an override), `env` (the deployment set it), `default` (built in). */
  source: 'admin' | 'env' | 'default';
  envDefault: InactivitySettingValue;
  updatedBy?: string;
  updatedAt?: string;
}

/** A partial settings update: a value sets an override, `null` resets it. */
export type InactivitySettingsPatch = Partial<Record<InactivitySettingField, InactivitySettingValue | null>>;

/** One audited change (`brewauth_inactivity_mode_changes`): a mode change,
 *  or (`kind: 'setting'`, brewauth#146) a settings change. */
export interface InactivityModeChange {
  kind: 'mode' | 'setting';
  /** Setting rows: the field, and its old/new values as JSON text. */
  field?: InactivitySettingField;
  oldValue?: string;
  newValue?: string;
  at: string;
  actorDn: string;
  from: InactivityMode;
  fromSource: 'env' | 'admin';
  to: InactivityMode;
  /** The server's own count of profiles the job would disable at that moment;
   *  undefined when that evaluation failed (see `previewError`). */
  wouldDisable?: number;
  previewError?: string;
  /** The grace period chosen with this change, if any. */
  graceDays?: number;
  graceUntil?: string;
}

/** `GET /admin/inactivity`: the mode in effect and the thresholds behind it. */
export interface InactivityStatus {
  /** False when brewauth's INACTIVITY_* configuration is invalid: nothing to
   *  preview or switch on (`unavailableReason` says why). */
  available: boolean;
  unavailableReason?: string;
  mode: InactivityMode;
  /** `env` until an administrator sets a mode; then `admin`. */
  source: 'env' | 'admin';
  envMode: InactivityMode;
  updatedBy?: string;
  updatedAt?: string;
  /** A grace period still in force: no account is disabled before
   *  `graceUntil`; affected accounts are warned instead. */
  graceDays?: number;
  graceUntil?: string;
  thresholdDays: number;
  warnDays: number[];
  maxDisablesPerTick: number;
  exemptCount: number;
  intervalSeconds: number;
  recentChanges: InactivityModeChange[];
  /** Every editable setting (brewauth#146); absent on an older brewauth. */
  settings?: Partial<Record<InactivitySettingField, InactivitySettingStatus>>;
}

/** One profile the job would disable right away. */
export interface InactivityPreviewEntry {
  profileId: string;
  label?: string;
  email?: string;
  subjectDn?: string;
  lastActivity: string;
  /** login | execute | dateActivated | dateCreated */
  lastActivitySource: string;
  daysInactive: number;
  /** Set while a grace period is in force: not disabled before this. */
  disableAfter?: string;
}

/** `GET /admin/inactivity/preview`: the job's own dry-run evaluation, now. */
export interface InactivityPreview {
  generatedAt: string;
  /** A global safety guard (thin LOGIN history, an implausible activity
   *  lookup) would hold the job off right now; the list is still computed. */
  suppressed: boolean;
  suppressedReason?: string;
  considered: number;
  wouldDisable: InactivityPreviewEntry[];
  graceUntil?: string;
  wouldWarn: number;
  skipped: number;
  exempted: number;
  adminsSkipped: number;
  errors: number;
}

/** A warning email on record for an account (brewauth `SentWarning`). */
export interface InactivitySentWarning {
  /** Days remaining when it was sent (5 / 3 / 1 with the shipped schedule). */
  warnDay: number;
  sentAt: string;
}

/** One account the job would disable within the window (brewauth#145). */
export interface InactivityUpcomingEntry {
  profileId: string;
  label?: string;
  email?: string;
  subjectDn?: string;
  lastActivity: string;
  lastActivitySource: string;
  daysInactive: number;
  /** When it falls due: the threshold date, or a grace period's end. */
  disableAt: string;
  dueNow: boolean;
  daysUntilDisable: number;
  inWarningWindow: boolean;
  graceApplied: boolean;
  /** Warnings sent in the current idle stretch, oldest first. */
  warningsSent: InactivitySentWarning[];
  /** Extra hourly runs the per-run cap pushes this disable back (estimate). */
  capDelayRuns: number;
}

export interface InactivityUpcomingCounts {
  dueNow: number;
  next24h: number;
  next7Days: number;
  inWarningWindow: number;
  total: number;
}

/** `GET /admin/inactivity/upcoming?within=N`: the job's own evaluation. */
export interface InactivityUpcoming {
  generatedAt: string;
  mode: InactivityMode;
  withinDays: number;
  thresholdDays: number;
  warnDays: number[];
  maxDisablesPerTick: number;
  graceUntil?: string;
  suppressed: boolean;
  suppressedReason?: string;
  counts: InactivityUpcomingCounts;
  upcoming: InactivityUpcomingEntry[];
}

export interface BrewauthAdminClient {
  /**
   * The public password-complexity policy (clients.yaml `password:` block),
   * from `GET /api/brewauth/api/reset/policy` (brewauth's `policyResponse`,
   * `internal/server/reset.go`). No session/CSRF/authRequestID required —
   * this is the same global policy brewauth's own `/ui/reset` confirm step
   * and `GET /api/signup/form`'s `passwordPolicy` block both render, just
   * flat instead of wrapped. Used to replace the gui-react password screens'
   * old `/api/info`-derived policy, which Java stops enforcing once
   * `createAccount` is deleted (jorigin/brewauth Java-auth-surface-cutover
   * Task 5/6) — rendering it here means the UI shows the rule brewauth
   * actually applies, not a stale one.
   */
  getPasswordPolicy(): Promise<BrewauthPasswordPolicy>;

  listByUser(profileId: string): Promise<AppGrant[]>;
  listByApp(clientId: string): Promise<AppGrant[]>;
  listPending(): Promise<AppGrant[]>;
  /** Pending-request count (`GET /pending/count`). Rejects with `status` 404 on an older brewauth. */
  countPending(): Promise<number>;
  listClients(): Promise<AppClient[]>;
  grant(profileId: string, clientId: string): Promise<void>; // also used to APPROVE a pending request
  revoke(profileId: string, clientId: string): Promise<void>;
  deny(profileId: string, clientId: string): Promise<void>;
  /** Sets a declared role, or the default with ''. Needs an approved grant. Also closes an open request. */
  setRole(profileId: string, clientId: string, role: string): Promise<void>;
  /** Open role requests. Rejects with `status` 404 on an older brewauth. */
  listRoleRequests(): Promise<AppGrant[]>;
  declineRoleRequest(profileId: string, clientId: string): Promise<void>;
  bulkGrant(
    clientId: string,
    profileIds: string[],
  ): Promise<{ granted: number; skipped: string[] }>;

  listSessions(filter?: SessionFilter): Promise<SessionListResult>;
  listSessionsByUser(profileId: string): Promise<BrewauthSession[]>;
  terminateSession(familyId: string): Promise<void>;
  revokeUserSessions(profileId: string): Promise<RevokeResult>;
  revokeAppSessions(clientId: string): Promise<RevokeResult>;
  listSessionEvents(profileId: string): Promise<BrewauthSessionEvent[]>;
  listHistorical(filter?: HistoricalFilter): Promise<HistoricalResult>;
  suggestSessions(
    field: 'subject' | 'host' | 'application',
    state?: 'active' | 'historical',
  ): Promise<string[]>;

  getRegistrationGlobal(): Promise<RegistrationDefinition | null>;
  putRegistrationGlobal(def: RegistrationDefinition): Promise<void>;
  getRegistrationApp(clientId: string): Promise<RegistrationDefinition | null>;
  putRegistrationApp(clientId: string, def: RegistrationDefinition): Promise<void>;
  deleteRegistrationApp(clientId: string): Promise<void>;
  getRegistrationEffective(clientId: string): Promise<RegistrationDefinition>;
  validateRegistration(
    def: RegistrationDefinition,
    clientId?: string,
  ): Promise<{ ok: boolean; errors: RegistrationValidationError[] }>;

  /**
   * Self-service "sign out of all devices": ends every SSO session and OIDC
   * token family belonging to the CALLING profile.
   *
   * Takes no profile id on purpose. brewauth identifies the caller from the
   * DN headers revproxy injects after introspecting this request's Bearer, so
   * the endpoint cannot be pointed at another account. It also revokes the
   * caller's own session, so the token in hand is dead once it returns —
   * callers should send the user to sign in rather than keep using it.
   */
  revokeAllMySessions(): Promise<void>;
  getProfileRegistration(profileId: string): Promise<ProfileRegistration>;
  putProfileRegistration(profileId: string, answers: RegistrationAnswers): Promise<void>;

  /**
   * Remove one credential from a profile. brewauth resolves the migration
   * target itself:
   *  - exactly one credential would remain -> removed, `targetDn` ignored/omitted.
   *  - more than one would remain -> rejects with a 400 whose thrown error
   *    carries `candidates` (see `CredentialCandidate`); retry with the
   *    admin's chosen `targetDn`.
   *  - this is the last credential -> rejects with a 409; the caller should
   *    offer profile deletion instead of hiding the control.
   * Removal is synchronous; the brewlytics/brewdb resource migration it
   * enqueues is not — it lands within a worker tick.
   *
   * Resolves with `CredentialRemoveResult`, whose `warning` field is set
   * when the removal committed but the migration enqueue failed — see that
   * type's doc comment. Callers must check it rather than assuming success.
   */
  removeCredential(
    profileId: string,
    subjectDn: string,
    targetDn?: string,
  ): Promise<CredentialRemoveResult>;
  /**
   * Force-expire one credential's password. The next login with it is
   * forced through the "password expired -> reset email" flow. No resource
   * migration is involved — the credential and its DN are unchanged.
   */
  expireCredential(profileId: string, subjectDn: string): Promise<void>;
  /**
   * Rewrite one credential's subject/issuer DN in place. The server
   * re-signs the stored entry and enqueues a brewdb/brewlytics resource
   * migration job for the old DN — the rename itself is synchronous, that
   * migration is not, so callers should not imply it has already finished.
   *
   * `next.subjectDn` is required even on an issuer-only reissue: the caller
   * resubmits the unchanged subject rather than brewauth inferring what
   * stayed the same. Rejects with 409 on a collision — this profile already
   * holds the target (subject, issuer) on another credential, or another
   * profile already holds the target subject DN — distinguished by the
   * thrown error's message.
   *
   * Resolves with `CredentialRenameResult`, whose `warning` field is set
   * when the rename committed but the migration enqueue failed — see that
   * type's doc comment. Callers must check it rather than assuming success.
   */
  updateCredentialDn(
    profileId: string,
    subjectDn: string,
    next: { subjectDn: string; issuerDn: string },
  ): Promise<CredentialRenameResult>;

  adminResetPasswordEmail(profileId: string, clientId?: string): Promise<void>;
  /**
   * Re-send the SIGNUP email-verification code for an account that is still
   * unconfirmed (`POST /admin/profiles/:id/verify-email/resend`,
   * `internal/server/admin.go`'s `resendVerifyEmail`, jorigin/brewauth#23) —
   * the admin-assist path for a user who cannot complete the self-service
   * `/api/verify/resend` themselves.
   *
   * Resolves by profile ID, never by the editable `Profile.Email`, matching
   * `adminResetPasswordEmail`. Re-issuing replaces any outstanding code and
   * resets its attempt counter, so an older code stops working.
   *
   * Unlike the public resend this one runs inline and reports real status
   * codes — the caller is an authenticated admin, so there is nothing to
   * hide behind anti-enumeration:
   *  - 403 when signup verification is disabled server-side;
   *  - 409 when the account has nothing to verify (already confirmed, or
   *    disabled/deleted);
   *  - 404 when the profile or its BASIC account is missing.
   * The 403/409 arrive as JSON `{error}` and so reach callers on the thrown
   * error's `serverMessage`; the 404s are plain text and do not (#39).
   */
  adminResendVerifyEmail(profileId: string, clientId?: string): Promise<void>;
  adminSetPassword(profileId: string, newPassword: string): Promise<void>;

  activateProfile(profileId: string, clientIds?: string[]): Promise<void>;
  disableProfile(profileId: string): Promise<void>;
  enableProfile(profileId: string): Promise<void>;

  createDeletionJob(
    profileId: string,
    opts: { assigneeId?: string; noTransfer?: boolean },
  ): Promise<{ job_id: string }>;
  listDeletionJobs(scope: 'active' | 'all'): Promise<DeletionJob[]>;
  // Paged sibling of listDeletionJobs — see that method's comment for why it
  // stays untouched. This is the one real pagination UI (the Lifecycle
  // sub-view, Task 5) should call.
  listDeletionJobsPage(
    scope: 'active' | 'all',
    opts?: { status?: string; mode?: string; profileId?: string; skip?: number; limit?: number },
  ): Promise<{ jobs: DeletionJob[]; total: number }>;
  getDeletionJob(jobId: string): Promise<{ job: DeletionJob; ops: DeletionOp[] }>;
  retryDeletionJob(jobId: string, participant?: string): Promise<void>;
  abortDeletionJob(jobId: string): Promise<void>;

  createMergeJob(sourceId: string, baseId: string): Promise<{ job_id: string }>;

  /** `GET /admin/inactivity` (administrators only). */
  getInactivity(): Promise<InactivityStatus>;
  /** `GET /admin/inactivity/preview`: no side effect. Rejects (502) rather
   *  than resolving an empty list when brewauth cannot evaluate. */
  previewInactivity(): Promise<InactivityPreview>;
  /** `PUT /admin/inactivity/mode`; brewauth audits the change. `changed` is
   *  false when that mode was already the administrator's setting.
   *  `graceDays` (enforcing only, 1-90): hold off disabling for that many
   *  days and warn the affected accounts instead. */
  /** `PUT /admin/inactivity/settings` (brewauth#146): partial; null resets. */
  setInactivitySettings(patch: InactivitySettingsPatch): Promise<void>;
  /** `POST /admin/inactivity/settings/preview`: who the patch would disable. Writes nothing. */
  previewInactivitySettings(patch: InactivitySettingsPatch): Promise<InactivityPreview>;
  /** `GET /admin/inactivity/upcoming?within=N` (brewauth#145): read-only. */
  getInactivityUpcoming(withinDays?: number): Promise<InactivityUpcoming>;
  setInactivityMode(
    mode: InactivityMode,
    opts?: { graceDays?: number },
  ): Promise<{ changed: boolean; mode: InactivityMode }>;
}

interface RawInactivityModeChange {
  kind?: string;
  field?: InactivitySettingField;
  old_value?: string;
  new_value?: string;
  at?: string;
  actor_dn?: string;
  from?: InactivityMode;
  from_source?: 'env' | 'admin';
  to?: InactivityMode;
  would_disable?: number;
  preview_error?: string;
  grace_days?: number;
  grace_until?: string;
}

interface RawInactivityStatus {
  available?: boolean;
  unavailable_reason?: string;
  mode?: InactivityMode;
  source?: 'env' | 'admin';
  env_mode?: InactivityMode;
  updated_by?: string;
  updated_at?: string;
  grace_days?: number;
  grace_until?: string;
  threshold_days?: number;
  warn_days?: number[] | null;
  max_disables_per_tick?: number;
  exempt_count?: number;
  interval_seconds?: number;
  recent_changes?: RawInactivityModeChange[] | null;
  settings?: Partial<Record<InactivitySettingField, {
    value?: InactivitySettingValue;
    source?: string;
    env_default?: InactivitySettingValue;
    updated_by?: string;
    updated_at?: string;
  }>> | null;
}

interface RawInactivityPreview {
  generated_at?: string;
  suppressed?: boolean;
  suppressed_reason?: string;
  considered?: number;
  would_disable?: Array<{
    profile_id?: string;
    label?: string;
    email?: string;
    subject_dn?: string;
    last_activity?: string;
    last_activity_source?: string;
    days_inactive?: number;
    disable_after?: string;
  }> | null;
  grace_until?: string;
  would_warn?: number;
  skipped?: number;
  exempted?: number;
  admins_skipped?: number;
  errors?: number;
}

function normInactivityChange(c: RawInactivityModeChange): InactivityModeChange {
  return {
    // Rows written before brewauth#146 carry no kind: they are mode changes.
    kind: c.kind === 'setting' ? 'setting' : 'mode',
    field: c.field || undefined,
    oldValue: c.old_value || undefined,
    newValue: c.new_value || undefined,
    at: String(c.at ?? ''),
    actorDn: String(c.actor_dn ?? ''),
    from: c.from ?? 'off',
    fromSource: c.from_source ?? 'env',
    to: c.to ?? 'off',
    wouldDisable: typeof c.would_disable === 'number' ? c.would_disable : undefined,
    previewError: c.preview_error || undefined,
    graceDays: c.grace_days || undefined,
    graceUntil: c.grace_until || undefined,
  };
}

function normInactivitySettings(
  raw: NonNullable<RawInactivityStatus['settings']>,
): Partial<Record<InactivitySettingField, InactivitySettingStatus>> {
  const out: Partial<Record<InactivitySettingField, InactivitySettingStatus>> = {};
  for (const [field, s] of Object.entries(raw) as [InactivitySettingField, NonNullable<typeof raw[InactivitySettingField]>][]) {
    if (s?.value === undefined) continue;
    out[field] = {
      value: s.value,
      source: s.source === 'admin' || s.source === 'env' ? s.source : 'default',
      envDefault: s.env_default ?? s.value,
      updatedBy: s.updated_by || undefined,
      updatedAt: s.updated_at || undefined,
    };
  }
  return out;
}

export function normInactivityStatus(raw: RawInactivityStatus): InactivityStatus {
  return {
    available: raw.available === true,
    unavailableReason: raw.unavailable_reason || undefined,
    // An unknown or missing mode reads as off: the page then offers to turn
    // it ON, which re-asks brewauth, rather than claiming it is enforcing.
    mode: raw.mode ?? 'off',
    source: raw.source === 'admin' ? 'admin' : 'env',
    envMode: raw.env_mode ?? 'off',
    updatedBy: raw.updated_by || undefined,
    updatedAt: raw.updated_at || undefined,
    graceDays: raw.grace_days || undefined,
    graceUntil: raw.grace_until || undefined,
    thresholdDays: raw.threshold_days ?? 0,
    warnDays: raw.warn_days ?? [],
    maxDisablesPerTick: raw.max_disables_per_tick ?? 0,
    exemptCount: raw.exempt_count ?? 0,
    intervalSeconds: raw.interval_seconds ?? 0,
    recentChanges: (raw.recent_changes ?? []).map(normInactivityChange),
    settings: raw.settings ? normInactivitySettings(raw.settings) : undefined,
  };
}

interface RawInactivityUpcoming {
  generated_at?: string;
  mode?: InactivityMode;
  within_days?: number;
  threshold_days?: number;
  warn_days?: number[] | null;
  max_disables_per_tick?: number;
  grace_until?: string;
  suppressed?: boolean;
  suppressed_reason?: string;
  counts?: { due_now?: number; next_24h?: number; next_7_days?: number; in_warning_window?: number; total?: number };
  upcoming?: Array<{
    profile_id?: string;
    label?: string;
    email?: string;
    subject_dn?: string;
    last_activity?: string;
    last_activity_source?: string;
    days_inactive?: number;
    disable_at?: string;
    due_now?: boolean;
    days_until_disable?: number;
    in_warning_window?: boolean;
    grace_applied?: boolean;
    warnings_sent?: Array<{ warn_day?: number; sent_at?: string }> | null;
    cap_delay_runs?: number;
  }> | null;
}

export function normInactivityUpcoming(raw: RawInactivityUpcoming): InactivityUpcoming {
  const c = raw.counts ?? {};
  return {
    generatedAt: String(raw.generated_at ?? ''),
    mode: raw.mode ?? 'off',
    withinDays: raw.within_days ?? 0,
    thresholdDays: raw.threshold_days ?? 0,
    warnDays: raw.warn_days ?? [],
    maxDisablesPerTick: raw.max_disables_per_tick ?? 0,
    graceUntil: raw.grace_until || undefined,
    suppressed: raw.suppressed === true,
    suppressedReason: raw.suppressed_reason || undefined,
    counts: {
      dueNow: c.due_now ?? 0,
      next24h: c.next_24h ?? 0,
      next7Days: c.next_7_days ?? 0,
      inWarningWindow: c.in_warning_window ?? 0,
      total: c.total ?? 0,
    },
    upcoming: (raw.upcoming ?? []).map((e) => ({
      profileId: String(e.profile_id ?? ''),
      label: e.label || undefined,
      email: e.email || undefined,
      subjectDn: e.subject_dn || undefined,
      lastActivity: String(e.last_activity ?? ''),
      lastActivitySource: String(e.last_activity_source ?? ''),
      daysInactive: e.days_inactive ?? 0,
      disableAt: String(e.disable_at ?? ''),
      dueNow: e.due_now === true,
      daysUntilDisable: e.days_until_disable ?? 0,
      inWarningWindow: e.in_warning_window === true,
      graceApplied: e.grace_applied === true,
      warningsSent: (e.warnings_sent ?? []).map((w) => ({ warnDay: w.warn_day ?? 0, sentAt: String(w.sent_at ?? '') })),
      capDelayRuns: e.cap_delay_runs ?? 0,
    })),
  };
}

export function normInactivityPreview(raw: RawInactivityPreview): InactivityPreview {
  return {
    generatedAt: String(raw.generated_at ?? ''),
    suppressed: raw.suppressed === true,
    suppressedReason: raw.suppressed_reason || undefined,
    considered: raw.considered ?? 0,
    wouldDisable: (raw.would_disable ?? []).map((e) => ({
      profileId: String(e.profile_id ?? ''),
      label: e.label || undefined,
      email: e.email || undefined,
      subjectDn: e.subject_dn || undefined,
      lastActivity: String(e.last_activity ?? ''),
      lastActivitySource: String(e.last_activity_source ?? ''),
      daysInactive: e.days_inactive ?? 0,
      disableAfter: e.disable_after || undefined,
    })),
    graceUntil: raw.grace_until || undefined,
    wouldWarn: raw.would_warn ?? 0,
    skipped: raw.skipped ?? 0,
    exempted: raw.exempted ?? 0,
    adminsSkipped: raw.admins_skipped ?? 0,
    errors: raw.errors ?? 0,
  };
}

/** Wire shape of brewauth's `jobIdentityView` — who a lifecycle job is
 *  about, resolved server-side from the shared profile document.
 *
 *  Only brewauth can answer this: a completed delete job's profile is a
 *  tombstone by construction, and every brewlytics profile search but an
 *  explicit `status=deleted` excludes tombstones, so resolving the name
 *  client-side could never work for the rows that most need it
 *  (jorigin/brewauth#84). `id` is always present, even when the profile
 *  document is gone entirely and nothing else could be resolved. */
export interface JobIdentity {
  id: string;
  /** Display name. A tombstone's keeps its " (Deleted)" suffix. */
  label?: string;
  /** Live email, or the tombstone's captured one. */
  email?: string;
  /** Credential subject DN, live or captured. */
  dn?: string;
  /** True for a tombstoned profile. */
  deleted?: boolean;
}

/** Wire shape of brewauth's `deletionJobView` (`internal/server/admin.go`) —
 *  snake_case json tags, matched verbatim (no PascalCase defense needed). */
export interface DeletionJob {
  id: string;
  profile_id: string;
  assignee_id: string;
  /** Set only for a credential-scoped job, whose `profile_id` is EMPTY —
   *  the profile that owns the affected credential. brewauth's own
   *  `deletionJobView` comment says in as many words that an admin UI must
   *  read this field rather than `profile_id` for those jobs. */
  cred_owner_profile_id?: string;
  status: string;
  /** 'delete' | 'merge' (brewauth `lifecycle.Mode`, Phase 6 Task 3) — optional
   *  so pre-Phase-6 fixtures/callers still typecheck; missing/unknown values
   *  render as the delete-mode default. */
  mode?: 'delete' | 'merge';
  /** Who this job is about, and who inherited the resources. `null` when the
   *  job names nobody (an unassigned delete has no assignee); absent from an
   *  older brewauth, which is why callers keep a client-side fallback. */
  subject?: JobIdentity | null;
  assignee?: JobIdentity | null;
  /** True when at least one participant op is dead-lettered: it exhausted
   *  its retries and the job cannot finish until an admin retries it
   *  (jorigin/brewauth#37). The three stuck fields are ABSENT from a
   *  brewauth that predates #37 — read them through `jobHelpers.serverStuck`,
   *  never as a bare truthiness check, so absent stays distinguishable from
   *  `false`. */
  has_deadlettered_ops?: boolean;
  /** The dead-lettered ops, ids only (brewauth never sends their error text
   *  here, since it can carry a DN). `[]` when nothing is dead-lettered. */
  deadlettered_ops?: DeadletteredOpRef[];
  /** The earliest `deadlettered_at` among `deadlettered_ops`; `null` when
   *  nothing is dead-lettered. */
  stuck_since?: string | null;
  created_at: string;
  updated_at: string;
}

/** One entry of `DeletionJob.deadlettered_ops` (brewauth's
 *  `deadletteredOpView`). */
export interface DeadletteredOpRef {
  id: string;
  /** Which participant's step failed: `brewdb`, `brewlytics`,
   *  `brewauth-identity`, … */
  participant: string;
  /** When the op dead-lettered — the transition's time, not when brewauth
   *  noticed it. */
  deadlettered_at: string;
}

/** Wire shape of brewauth's `deletionOpView` — one per-participant step of a
 *  deletion job (see `DeletionJob`'s module note above). */
export interface DeletionOp {
  id: string;
  job_id: string;
  profile_id: string;
  assignee_id: string;
  participant: string;
  status: string;
  /** 'delete' | 'merge' — see `DeletionJob.mode`. */
  mode?: 'delete' | 'merge';
  attempts: number;
  lease_owner: string;
  lease_until: string;
  last_error: string;
  /** Set only while this op is dead-lettered, by a brewauth with
   *  jorigin/brewauth#37; absent otherwise. */
  deadlettered_at?: string;
  created_at: string;
  updated_at: string;
}

/** One active application session (a live refresh-token family). */
export interface BrewauthSession {
  familyId: string;
  profileId: string;
  subjectDn: string;
  activeTokenId: string;
  credentialSubject: string;
  credentialIssuer: string;
  clientId: string;
  method: string;
  host: string;
  clientIp: string;
  userAgent: string;
  issuedAt: string;
  firstLogin: string;
  lastSeen: string;
  accessExpiresAt: string;
  idleDeadline: string;
  maxLifetime: string;
}

export interface SessionFilter {
  subject?: string;
  host?: string;
  application?: string;
  method?: string;
  skip?: number;
  limit?: number;
}

export interface SessionListResult {
  sessions: BrewauthSession[];
  /** Authoritative filtered pre-pagination count from the backend (see the
   *  module header). Falls back to `sessions.length` only defensively,
   *  against older backends whose response omits `total`. */
  total: number;
}

export interface RevokeResult {
  revoked: number;
}

/**
 * One session lifecycle event. `at`/`type`/`reason`/`actor`/`clientId`/
 * `familyId` are always present (both `listSessionEvents` and
 * `listHistorical` supply them); the rest are only present on rows from
 * `listHistorical`'s richer `historicalEventView`.
 */
export interface BrewauthSessionEvent {
  at: string;
  type: string;
  reason: string;
  actor: string;
  clientId: string;
  familyId: string;
  profileId?: string;
  subjectDn?: string;
  method?: string;
  host?: string;
  clientIp?: string;
  userAgent?: string;
  issuedAt?: string;
  firstLogin?: string;
  expiresAt?: string;
  lastSeen?: string;
}

export interface HistoricalFilter extends SessionFilter {
  eventType?: string;
  reason?: string;
  since?: string;
  until?: string;
}

export interface HistoricalResult {
  events: BrewauthSessionEvent[];
  total: number;
  byEventType: Record<string, number>;
  byReason: Record<string, number>;
}

export interface BrewauthAdminConfig {
  baseUrl: string;
  getToken: () => Promise<string | null>;
  /**
   * Optional 401 recovery ladder. These admin calls use raw `fetch`, so they
   * bypass the app's axios 401 interceptor; without this hook a session that
   * has expired (e.g. idle timeout) just throws to the caller and the re-auth
   * popup never fires. Given the token the request was rejected with
   * (`staleToken`, so a silent renew can tell a dead token from one another tab
   * already rotated) and whether the server flagged it revoked, it returns
   * `true` if the session was recovered (retry the request once) or `false`
   * (surface the 401). Omit it and 401s propagate unchanged.
   */
  onUnauthorized?: (staleToken: string | undefined, revoked: boolean) => Promise<boolean>;
}

/**
 * Wire shape of a grant record as brewauth actually serializes it today:
 * snake_case keys (`profile_id`, `client_id`, `granted_at`, ...), with
 * `granted_at`/`requested_at` always present and `null` meaning "unknown"
 * rather than a real timestamp (see the module header). The camelCase/
 * PascalCase variants below are retained defensively for compatibility with
 * older server builds that predate the snake_case switch, not because the
 * current server sends them. Response JSON is untrusted, so every field is
 * `unknown`-typed and narrowed in `norm`.
 */
interface RawGrant {
  profileId?: unknown;
  ProfileID?: unknown;
  profile_id?: unknown;
  clientId?: unknown;
  ClientID?: unknown;
  client_id?: unknown;
  status?: unknown;
  Status?: unknown;
  grantedBy?: unknown;
  GrantedBy?: unknown;
  granted_by?: unknown;
  grantedAt?: unknown;
  GrantedAt?: unknown;
  granted_at?: unknown;
  justification?: unknown;
  Justification?: unknown;
  requestedAt?: unknown;
  RequestedAt?: unknown;
  requested_at?: unknown;
  role?: unknown;
  role_request?: unknown;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

/**
 * Wire shape of `GET /api/reset/policy`'s `policyResponse` — already
 * camelCase on the wire (unlike the snake_case admin endpoints elsewhere in
 * this file), so `normPasswordPolicy` below defends against a missing/
 * malformed field rather than remapping keys.
 */
export interface BrewauthPasswordPolicy {
  minLength: number;
  minUppercase: number;
  minLowercase: number;
  minDigits: number;
  minSpecial: number;
  maxLength: number;
  specialChars: string;
}

interface RawPasswordPolicy {
  minLength?: unknown;
  minUppercase?: unknown;
  minLowercase?: unknown;
  minDigits?: unknown;
  minSpecial?: unknown;
  maxLength?: unknown;
  specialChars?: unknown;
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function normPasswordPolicy(r: RawPasswordPolicy): BrewauthPasswordPolicy {
  return {
    minLength: num(r.minLength),
    minUppercase: num(r.minUppercase),
    minLowercase: num(r.minLowercase),
    minDigits: num(r.minDigits),
    minSpecial: num(r.minSpecial),
    maxLength: num(r.maxLength),
    specialChars: str(r.specialChars) ?? '',
  };
}

function normRoleRequest(raw: unknown): AppRoleRequest | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const r = raw as { role?: unknown; justification?: unknown; requested_at?: unknown };
  const role = str(r.role);
  if (!role) return undefined;
  return { role, justification: str(r.justification), requestedAt: str(r.requested_at) };
}

/** Response JSON is untrusted: a malformed block reads as no roles, so no control shows. */
function normClientRoles(raw: unknown): AppClientRoles | undefined {
  const roleOf = (v: unknown): AppRole | undefined => {
    if (typeof v !== 'object' || v === null) return undefined;
    const r = v as { id?: unknown; label?: unknown };
    const id = str(r.id);
    return id ? { id, label: str(r.label) || id } : undefined;
  };
  if (typeof raw !== 'object' || raw === null) return undefined;
  const block = raw as { default?: unknown; assignable?: unknown };
  const def = roleOf(block.default);
  const assignable = Array.isArray(block.assignable)
    ? block.assignable.map(roleOf).filter((r): r is AppRole => r !== undefined)
    : [];
  return def && assignable.length > 0 ? { default: def, assignable } : undefined;
}

function norm(g: RawGrant): AppGrant {
  return {
    profileId: str(g.profileId ?? g.ProfileID ?? g.profile_id) ?? '',
    clientId: str(g.clientId ?? g.ClientID ?? g.client_id) ?? '',
    status: (str(g.status ?? g.Status) ?? 'pending') as AppGrant['status'],
    grantedBy: str(g.grantedBy ?? g.GrantedBy ?? g.granted_by),
    grantedAt: str(g.grantedAt ?? g.GrantedAt ?? g.granted_at),
    justification: str(g.justification ?? g.Justification),
    requestedAt: str(g.requestedAt ?? g.RequestedAt ?? g.requested_at),
    role: str(g.role) || undefined,
    roleRequest: normRoleRequest(g.role_request),
  };
}

/** Wire shape of `sessionView` — fixed snake_case, see module header. */
interface RawSession {
  family_id: string;
  profile_id: string;
  subject_dn: string;
  active_token_id: string;
  credential_subject: string;
  credential_issuer: string;
  client_id: string;
  method: string;
  host: string;
  client_ip: string;
  user_agent: string;
  issued_at: string;
  first_login: string;
  last_seen: string;
  access_expires_at: string;
  idle_deadline: string;
  max_lifetime: string;
}

function normSession(r: RawSession): BrewauthSession {
  return {
    familyId: r.family_id,
    profileId: r.profile_id,
    subjectDn: r.subject_dn,
    activeTokenId: r.active_token_id,
    credentialSubject: r.credential_subject,
    credentialIssuer: r.credential_issuer,
    clientId: r.client_id,
    method: r.method,
    host: r.host,
    clientIp: r.client_ip,
    userAgent: r.user_agent,
    issuedAt: r.issued_at,
    firstLogin: r.first_login,
    lastSeen: r.last_seen,
    accessExpiresAt: r.access_expires_at,
    idleDeadline: r.idle_deadline,
    maxLifetime: r.max_lifetime,
  };
}

/** Wire shape of the compact `eventView` (`GET .../events`). */
interface RawEventCompact {
  at: string;
  client_id: string;
  family_id: string;
  type: string;
  reason: string;
  actor: string;
}

function normEventCompact(r: RawEventCompact): BrewauthSessionEvent {
  return {
    at: r.at,
    clientId: r.client_id,
    familyId: r.family_id,
    type: r.type,
    reason: r.reason,
    actor: r.actor,
  };
}

/** Wire shape of the rich `historicalEventView` (`GET .../historical`). */
interface RawEventRich {
  at: string;
  type: string;
  reason: string;
  actor: string;
  profile_id: string;
  subject_dn: string;
  client_id: string;
  method: string;
  host: string;
  client_ip: string;
  user_agent: string;
  family_id: string;
  issued_at: string;
  first_login: string;
  expires_at: string;
  last_seen: string;
}

function normEventRich(r: RawEventRich): BrewauthSessionEvent {
  return {
    at: r.at,
    type: r.type,
    reason: r.reason,
    actor: r.actor,
    clientId: r.client_id,
    familyId: r.family_id,
    profileId: r.profile_id,
    subjectDn: r.subject_dn,
    method: r.method,
    host: r.host,
    clientIp: r.client_ip,
    userAgent: r.user_agent,
    issuedAt: r.issued_at,
    firstLogin: r.first_login,
    expiresAt: r.expires_at,
    lastSeen: r.last_seen,
  };
}

/** Wire shape of `credentialView` — fixed snake_case, see `CredentialCandidate`. */
interface RawCredentialCandidate {
  subject_dn?: unknown;
  issuer_dn?: unknown;
  is_basic?: unknown;
  password_expired?: unknown;
}

function normCredentialCandidate(c: RawCredentialCandidate): CredentialCandidate {
  return {
    subjectDn: str(c.subject_dn) ?? '',
    issuerDn: str(c.issuer_dn) ?? '',
    isBasic: c.is_basic === true,
    passwordExpired: c.password_expired === true,
  };
}

/**
 * Builds a `?`-prefixed query string from an ordered set of params,
 * omitting undefined/empty values. Key order follows the object's
 * insertion order, which callers fix to match the backend's documented
 * param order so resolved URLs are deterministic.
 */
function buildQuery(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    parts.push(`${key}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

function sessionQuery(filter?: SessionFilter): string {
  return buildQuery({
    subject: filter?.subject,
    host: filter?.host,
    application: filter?.application,
    method: filter?.method,
    skip: filter?.skip,
    limit: filter?.limit,
  });
}

function historicalQuery(filter?: HistoricalFilter): string {
  return buildQuery({
    subject: filter?.subject,
    host: filter?.host,
    application: filter?.application,
    method: filter?.method,
    event_type: filter?.eventType,
    reason: filter?.reason,
    since: filter?.since,
    until: filter?.until,
    skip: filter?.skip,
    limit: filter?.limit,
  });
}

function normRevoke(data: { revoked?: unknown } | null): RevokeResult {
  return { revoked: typeof data?.revoked === 'number' ? data.revoked : 0 };
}

export function createBrewauthAdminClient(cfg: BrewauthAdminConfig): BrewauthAdminClient {
  const base = cfg.baseUrl.replace(/\/$/, '') + '/api/brewauth/admin/app-access';
  // Sessions live at a sibling path, /api/brewauth/admin/sessions*, not
  // nested under app-access — hence a separate base + req variant below.
  const adminBase = cfg.baseUrl.replace(/\/$/, '') + '/api/brewauth/admin';
  // Profile registration lives outside /admin — a profile reads/writes its
  // own registration answers at /api/brewauth/profiles/:id/registration.
  const apiBase = cfg.baseUrl.replace(/\/$/, '') + '/api/brewauth';

  async function reqAt<T>(
    urlBase: string,
    method: string,
    path: string,
    body?: unknown,
    retried = false,
  ): Promise<T> {
    const token = await cfg.getToken();
    const res = await fetch(urlBase + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    // 401 recovery ladder (mirrors the axios interceptor): try to recover the
    // session once, then retry, instead of throwing straight to the caller.
    // A server-flagged revoked token is passed through so the ladder can skip a
    // silent refresh that would just fail.
    if (res.status === 401 && !retried && cfg.onUnauthorized) {
      const revoked = String(res.headers.get('x-auth-status') ?? '').toUpperCase() === 'REVOKED';
      if (await cfg.onUnauthorized(token ?? undefined, revoked)) {
        return reqAt<T>(urlBase, method, path, body, true);
      }
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let message = `brewauth admin ${method} ${path} -> ${res.status}`;
      // removeCredential's 400 (ambiguous migration target) lists survivor
      // candidates alongside {error} so the caller can raise a target-picker
      // without a second round-trip. Only present on that one response shape;
      // undefined everywhere else.
      let candidates: CredentialCandidate[] | undefined;
      // Whether the SERVER supplied this message, as opposed to the generic
      // fallback above. Callers cannot tell from `message` alone, and the
      // difference matters: brewauth sends some 4xx as JSON {error} and
      // others as plain text (jorigin/brewauth#39), so a caller that always
      // preferred `message` would replace a good hardcoded string with a
      // useless "brewauth admin POST /… -> 409" on the plain-text routes.
      // With this flag a caller can use the server's wording when there is
      // one and keep its own fallback otherwise.
      let serverMessage: string | undefined;
      try {
        const body = JSON.parse(text);
        if (body && typeof body.error === 'string' && body.error) {
          message = body.error;
          serverMessage = body.error;
        }
        if (body && Array.isArray(body.candidates)) {
          candidates = (body.candidates as RawCredentialCandidate[]).map(normCredentialCandidate);
        }
      } catch {
        /* not JSON — keep the generic message */
      }
      throw Object.assign(new Error(message), { status: res.status, candidates, serverMessage });
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
    return reqAt<T>(base, method, path, body);
  }

  async function reqSessions<T>(method: string, path: string, body?: unknown): Promise<T> {
    return reqAt<T>(adminBase, method, path, body);
  }

  async function reqApi<T>(method: string, path: string, body?: unknown): Promise<T> {
    return reqAt<T>(apiBase, method, path, body);
  }

  // Registration definitions live under /api/brewauth/admin/registration.
  async function reqReg<T>(method: string, path: string, body?: unknown): Promise<T> {
    return reqAt<T>(adminBase, method, '/registration' + path, body);
  }
  // GET a definition that may not exist yet: reqAt throws with a 404 status
  // on a missing definition; translate only that to null. Keyed off the
  // status reqAt attaches to the thrown Error, not the message text — the
  // message may be a server-supplied {error} body (e.g. "no global
  // definition") rather than the generic "-> 404" text.
  async function reqRegOrNull<T>(path: string): Promise<T | null> {
    try {
      return await reqReg<T>('GET', path);
    } catch (e) {
      if (e && typeof e === 'object' && (e as { status?: number }).status === 404) return null;
      throw e;
    }
  }

  async function list(path: string): Promise<AppGrant[]> {
    const raw = await req<RawGrant[] | null>('GET', path);
    return (raw ?? []).map(norm);
  }

  return {
    // apiBase already ends in /api/brewauth; the '/api/reset/policy' path
    // below lands on /api/brewauth/api/reset/policy — the doubled "api" is
    // correct, not a typo: revproxy strips /api/brewauth/ before forwarding
    // to brewauth's root, so the remainder must still start with brewauth's
    // own /api/ prefix.
    getPasswordPolicy: async () => {
      const data = await reqApi<RawPasswordPolicy | null>('GET', '/api/reset/policy');
      return normPasswordPolicy(data ?? {});
    },

    listByUser: (profileId) => list(`/user/${encodeURIComponent(profileId)}`),
    listByApp: (clientId) => list(`/app/${encodeURIComponent(clientId)}`),
    listPending: () => list('/pending'),

    countPending: async () => {
      const body = await req<{ count?: unknown } | null>('GET', '/pending/count');
      const n = body?.count;
      if (typeof n !== 'number' || !Number.isInteger(n) || n < 0) {
        throw new Error('brewauth admin GET /pending/count -> malformed count');
      }
      return n;
    },

    listClients: async () => {
      const body = await reqAt<Array<{ id?: string; name?: string; app_url?: string; roles?: unknown }> | null>(
        adminBase,
        'GET',
        '/clients',
      );
      return (body ?? []).map((c) => ({
        id: String(c.id ?? ''),
        name: String(c.name || c.id || ''),
        appUrl: c.app_url ? String(c.app_url) : undefined,
        roles: normClientRoles(c.roles),
      }));
    },

    // POST to the collection base (no trailing slash): `.../app-access`.
    grant: (profileId, clientId) =>
      req<void>('POST', '', { profile_id: profileId, client_id: clientId }),

    revoke: (profileId, clientId) =>
      req<void>('DELETE', `/${encodeURIComponent(profileId)}/${encodeURIComponent(clientId)}`),

    deny: (profileId, clientId) =>
      req<void>('POST', '/deny', { profile_id: profileId, client_id: clientId }),

    setRole: (profileId, clientId, role) =>
      req<void>('PUT', `/${encodeURIComponent(profileId)}/${encodeURIComponent(clientId)}/role`, { role }),

    listRoleRequests: () => list('/role-requests'),

    declineRoleRequest: (profileId, clientId) =>
      req<void>('POST', `/${encodeURIComponent(profileId)}/${encodeURIComponent(clientId)}/role-request/decline`),

    bulkGrant: (clientId, profileIds) =>
      req<{ granted: number; skipped: string[] }>('POST', '/bulk', {
        client_id: clientId,
        profile_ids: profileIds,
      }),

    listSessions: async (filter) => {
      const data = await reqSessions<{ sessions?: RawSession[]; total?: number } | null>(
        'GET',
        '/sessions' + sessionQuery(filter),
      );
      const sessions = (data?.sessions ?? []).map(normSession);
      return {
        sessions,
        total: typeof data?.total === 'number' ? data.total : sessions.length,
      };
    },

    listSessionsByUser: async (profileId) => {
      const data = await reqSessions<{ sessions?: RawSession[] } | null>(
        'GET',
        `/sessions/user/${encodeURIComponent(profileId)}`,
      );
      return (data?.sessions ?? []).map(normSession);
    },

    terminateSession: (familyId) =>
      reqSessions<void>('DELETE', `/sessions/${encodeURIComponent(familyId)}`),

    revokeUserSessions: async (profileId) => {
      const data = await reqSessions<{ revoked?: unknown } | null>(
        'DELETE',
        `/sessions/user/${encodeURIComponent(profileId)}`,
      );
      return normRevoke(data);
    },

    revokeAppSessions: async (clientId) => {
      const data = await reqSessions<{ revoked?: unknown } | null>(
        'DELETE',
        `/sessions/app/${encodeURIComponent(clientId)}`,
      );
      return normRevoke(data);
    },

    listSessionEvents: async (profileId) => {
      const data = await reqSessions<{ events?: RawEventCompact[] } | null>(
        'GET',
        `/sessions/user/${encodeURIComponent(profileId)}/events`,
      );
      return (data?.events ?? []).map(normEventCompact);
    },

    listHistorical: async (filter) => {
      const data = await reqSessions<{
        events?: RawEventRich[];
        total?: number;
        by_event_type?: Record<string, number>;
        by_reason?: Record<string, number>;
      } | null>('GET', '/sessions/historical' + historicalQuery(filter));
      return {
        events: (data?.events ?? []).map(normEventRich),
        total: data?.total ?? 0,
        byEventType: data?.by_event_type ?? {},
        byReason: data?.by_reason ?? {},
      };
    },

    suggestSessions: async (field, state) => {
      const data = await reqSessions<{ values?: string[] } | null>(
        'GET',
        '/sessions/suggest' + buildQuery({ field, state }),
      );
      return data?.values ?? [];
    },

    getRegistrationGlobal: () => reqRegOrNull<RegistrationDefinition>('/global'),
    putRegistrationGlobal: (def) => reqReg<void>('PUT', '/global', def),
    getRegistrationApp: (clientId) =>
      reqRegOrNull<RegistrationDefinition>(`/app/${encodeURIComponent(clientId)}`),
    putRegistrationApp: (clientId, def) =>
      reqReg<void>('PUT', `/app/${encodeURIComponent(clientId)}`, def),
    deleteRegistrationApp: (clientId) =>
      reqReg<void>('DELETE', `/app/${encodeURIComponent(clientId)}`),
    getRegistrationEffective: (clientId) =>
      reqReg<RegistrationDefinition>('GET', `/effective/${encodeURIComponent(clientId)}`),
    validateRegistration: async (def, clientId) => {
      const r = await reqReg<{ ok?: boolean; errors?: RegistrationValidationError[] | null }>(
        'POST',
        '/validate' + (clientId ? `?client_id=${encodeURIComponent(clientId)}` : ''),
        def,
      );
      // brewauth marshals an empty Go slice to JSON `null`, not `[]`; normalize
      // so callers always get an array (the page does serverErrors.length/.map).
      return { ok: !!r?.ok, errors: r?.errors ?? [] };
    },

    revokeAllMySessions: () => reqApi<void>('POST', '/api/sessions/revoke-all'),

    getProfileRegistration: (profileId) =>
      reqApi<ProfileRegistration>('GET', `/profiles/${encodeURIComponent(profileId)}/registration`),
    putProfileRegistration: (profileId, answers) =>
      reqApi<void>('PUT', `/profiles/${encodeURIComponent(profileId)}/registration`, { answers }),

    removeCredential: (profileId, subjectDn, targetDn) =>
      reqAt<CredentialRemoveResult>(
        adminBase,
        'DELETE',
        `/profiles/${encodeURIComponent(profileId)}/credentials/${encodeURIComponent(subjectDn)}` +
          (targetDn ? `?target=${encodeURIComponent(targetDn)}` : ''),
      ),
    expireCredential: (profileId, subjectDn) =>
      reqAt<void>(
        adminBase,
        'POST',
        `/profiles/${encodeURIComponent(profileId)}/credentials/${encodeURIComponent(subjectDn)}/expire`,
      ),
    updateCredentialDn: (profileId, subjectDn, next) =>
      reqAt<CredentialRenameResult>(
        adminBase,
        'PUT',
        `/profiles/${encodeURIComponent(profileId)}/credentials/${encodeURIComponent(subjectDn)}`,
        { subject: next.subjectDn, issuer: next.issuerDn },
      ),

    adminResetPasswordEmail: (profileId, clientId) =>
      reqAt<void>(
        adminBase,
        'POST',
        `/profiles/${encodeURIComponent(profileId)}/password/reset-email`,
        clientId ? { client_id: clientId } : undefined,
      ),
    adminResendVerifyEmail: (profileId, clientId) =>
      reqAt<void>(
        adminBase,
        'POST',
        `/profiles/${encodeURIComponent(profileId)}/verify-email/resend`,
        clientId ? { client_id: clientId } : undefined,
      ),
    adminSetPassword: (profileId, newPassword) =>
      reqAt<void>(adminBase, 'POST', `/profiles/${encodeURIComponent(profileId)}/password/set`, {
        password: newPassword,
      }),

    activateProfile: (profileId, clientIds) =>
      reqAt<void>(
        adminBase,
        'POST',
        `/profiles/${encodeURIComponent(profileId)}/activate`,
        clientIds?.length ? { clientIds } : undefined,
      ),
    disableProfile: (profileId) =>
      reqAt<void>(adminBase, 'POST', `/profiles/${encodeURIComponent(profileId)}/disable`),

    getInactivity: async () =>
      normInactivityStatus((await reqAt<RawInactivityStatus | null>(adminBase, 'GET', '/inactivity')) ?? {}),
    previewInactivity: async () =>
      normInactivityPreview(
        (await reqAt<RawInactivityPreview | null>(adminBase, 'GET', '/inactivity/preview')) ?? {},
      ),
    setInactivitySettings: async (patch) => {
      await reqAt<unknown>(adminBase, 'PUT', '/inactivity/settings', patch);
    },
    previewInactivitySettings: async (patch) =>
      normInactivityPreview(
        (await reqAt<RawInactivityPreview | null>(adminBase, 'POST', '/inactivity/settings/preview', patch)) ?? {},
      ),
    getInactivityUpcoming: async (withinDays) =>
      normInactivityUpcoming(
        (await reqAt<RawInactivityUpcoming | null>(
          adminBase,
          'GET',
          '/inactivity/upcoming' + buildQuery({ within: withinDays }),
        )) ?? {},
      ),
    setInactivityMode: async (mode, opts) => {
      const data = await reqAt<{ changed?: boolean; mode?: InactivityMode } | null>(
        adminBase,
        'PUT',
        '/inactivity/mode',
        opts?.graceDays ? { mode, grace_days: opts.graceDays } : { mode },
      );
      return { changed: data?.changed === true, mode: data?.mode ?? mode };
    },
    enableProfile: (profileId) =>
      reqAt<void>(adminBase, 'POST', `/profiles/${encodeURIComponent(profileId)}/enable`),

    createDeletionJob: (profileId, opts) => {
      let qs = '';
      if (opts.assigneeId) qs = `?assignee=${encodeURIComponent(opts.assigneeId)}`;
      else if (opts.noTransfer) qs = '?no_transfer=true';
      return reqAt<{ job_id: string }>(
        adminBase,
        'DELETE',
        `/profiles/${encodeURIComponent(profileId)}${qs}`,
      );
    },
    listDeletionJobs: async (scope) => {
      // The list endpoint returns {jobs, total}; older builds returned a
      // bare array. Accept both so this client isn't pinned to one server
      // version.
      //
      // limit=200 is explicit and intentional: brewauth's list endpoint now
      // defaults to 50 (lifecycle.DefaultJobLimit) when no limit is given,
      // but this UI has no pagination controls yet — it renders whatever
      // page it gets as the complete set. 200 is brewauth's MaxJobLimit
      // ceiling (lifecycle.MaxJobLimit), the same cap `scope=all` was
      // already clamped to before the server grew pagination, so this keeps
      // today's behavior unchanged. Do not remove this once the UI grows
      // real pagination — until then it's load-bearing, not leftover.
      const body = await reqAt<DeletionJob[] | { jobs?: DeletionJob[] } | null>(
        adminBase,
        'GET',
        `/profile-deletions?scope=${scope}&limit=200`,
      );
      return Array.isArray(body) ? body : (body?.jobs ?? []);
    },
    listDeletionJobsPage: async (scope, opts) => {
      const q = new URLSearchParams({ scope });
      if (opts?.status) q.set('status', opts.status);
      if (opts?.mode) q.set('mode', opts.mode);
      if (opts?.profileId) q.set('profile_id', opts.profileId);
      if (opts?.skip) q.set('skip', String(opts.skip));
      q.set('limit', String(opts?.limit ?? 25));
      const body = await reqAt<{ jobs?: DeletionJob[]; total?: number } | DeletionJob[] | null>(
        adminBase,
        'GET',
        `/profile-deletions?${q.toString()}`,
      );
      // Tolerate the legacy bare-array shape the same way listDeletionJobs does.
      if (Array.isArray(body)) return { jobs: body, total: body.length };
      return { jobs: body?.jobs ?? [], total: body?.total ?? 0 };
    },
    getDeletionJob: (jobId) =>
      reqAt<{ job: DeletionJob; ops: DeletionOp[] }>(
        adminBase,
        'GET',
        `/profile-deletions/${encodeURIComponent(jobId)}`,
      ),
    retryDeletionJob: (jobId, participant) =>
      reqAt<void>(
        adminBase,
        'POST',
        `/profile-deletions/${encodeURIComponent(jobId)}/retry` +
          (participant ? `?participant=${encodeURIComponent(participant)}` : ''),
      ),
    abortDeletionJob: (jobId) =>
      reqAt<void>(adminBase, 'POST', `/profile-deletions/${encodeURIComponent(jobId)}/abort`),

    createMergeJob: (sourceId, baseId) =>
      reqAt<{ job_id: string }>(
        adminBase,
        'POST',
        `/profiles/${encodeURIComponent(sourceId)}/merge?into=${encodeURIComponent(baseId)}`,
      ),
  };
}

// PROMOTE-LATER: extract this file to a shared @brew/brewauth-client package
// (brewlytics#... follow-up). Keep it dependency-free.
